import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * A NEW PAGE STARTS AT THE TOP (walkthrough audit B5). The app runs on
 * <BrowserRouter>, which has no ScrollRestoration (that needs a data router),
 * and nothing reset the scroll: "Edit schedule" opened the listening schedule
 * at the bottom and a created circle opened past its header.
 *
 * - PUSH or REPLACE to another pathname: scroll to the top. REPLACE too, on
 *   purpose (accepted in review round 1 on #1079): a replace to another
 *   pathname is a new page (the login / register and <Navigate> redirects,
 *   quiz to its results, the onboarding guard), so it opens at the top like
 *   a push. Replaces on the same pathname (tab and filter routers) do not
 *   scroll, per the rule below. The name says Push for the common case.
 * - POP (Back / Forward): leave it; the browser restores the position.
 * - Same pathname (a query string or hash, /app/empathy?tab=1on1): leave it.
 * - First render: leave it, so a reload keeps its place (the router reports
 *   the initial location as a POP).
 *
 * Mounted once in Providers, before the routes, so its effect runs before a
 * page's own mount effects: a page that scrolls itself to an anchor (the
 * course's next lesson) still wins. The article page keeps its own reset,
 * which also covers a POP between two articles (new content, same scroller).
 */
export function ScrollToTopOnPush(): null {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef(pathname);

  useEffect(() => {
    if (previous.current === pathname) return;
    previous.current = pathname;
    if (navigationType === 'POP') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, navigationType]);

  return null;
}
